import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  STAKEHOLDER_STALE_DAYS,
  STAKEHOLDER_TAG_LABEL,
  STAKEHOLDER_TAG_ORDER,
  type StakeholderDetail,
  type StakeholderStats,
  type StakeholderSummary,
  type StakeholderTag,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { stakeholderApi } from '../api/stakeholder.api';
import { SidePanel } from '../components/SidePanel';
import { Pagination } from '../components/ui';
import { StakeholderPanel, TAG_CHIP, contactAge } from '../components/StakeholderPanel';

/**
 * Stakeholders - people OUTSIDE the organisation who are connected to it.
 *
 * Admin and Super Admin only. The sidebar hides the link for everyone else,
 * but the API's 403 is the actual control - this page would 403 if reached
 * by URL.
 *
 * FILTERING IS SERVER-SIDE here, unlike the calendar: the contact list
 * grows without bound and is paginated, so filtering in memory would only
 * ever filter the page you happen to be on.
 */

/** The API caps pageSize at 100; these are the offered steps. */
const PAGE_SIZES = [20, 50, 100];

export default function StakeholdersPage() {
  const [rows, setRows] = useState<StakeholderSummary[]>([]);
  const [stats, setStats] = useState<StakeholderStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]!);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<StakeholderTag[]>([]);
  const [staleOnly, setStaleOnly] = useState(false);

  const [selected, setSelected] = useState<StakeholderDetail | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await stakeholderApi.list({
        search: search.trim() || undefined,
        tags: tagFilter.length ? tagFilter : undefined,
        staleDays: staleOnly ? STAKEHOLDER_STALE_DAYS : undefined,
        page,
        pageSize,
      });
      setRows(result.rows);
      setTotal(result.meta.total);

      /**
       * Deleting the last row of the last page, or narrowing a filter,
       * can leave you standing on a page that no longer exists - which
       * looks like "no contacts match" rather than "you are past the end".
       * Step back and the effect re-runs.
       */
      const pages = Math.max(1, Math.ceil(result.meta.total / pageSize));
      if (page > pages) setPage(pages);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load contacts.');
    } finally {
      setLoading(false);
    }
  }, [search, tagFilter, staleOnly, page, pageSize]);

  // Debounced: typing a name should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    stakeholderApi.stats().then(setStats).catch(() => setStats(null));
  }, []);

  /** Any filter or size change returns to page 1 - page 4 of a new filter
   *  is usually empty, and landing there reads as "nothing found". */
  useEffect(() => { setPage(1); }, [search, tagFilter, staleOnly, pageSize]);

  function refreshAfterWrite(updated: StakeholderDetail) {
    setSelected(updated);
    setRows((current) => current.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    stakeholderApi.stats().then(setStats).catch(() => undefined);
  }

  const toggleTag = (tag: StakeholderTag) =>
    setTagFilter((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    );

  return (
    <div className="mx-auto max-w-6xl px-4 py-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Guests</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            People outside the organisation - investors, party members,
            constituents, media and donors.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="shrink-0 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          + Add contact
        </button>
      </div>

      {/* Org-wide counts, not this page's - a number that changes when you
          turn the page is not a number anyone can quote. */}
      {stats && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Stat label="Contacts" value={stats.total} />
          {STAKEHOLDER_TAG_ORDER.map((t) => (
            <Stat key={t} label={STAKEHOLDER_TAG_LABEL[t]} value={stats.byTag[t]} />
          ))}
          <Stat
            label={`Quiet ${STAKEHOLDER_STALE_DAYS}d+`}
            value={stats.needsFollowUp}
            tone={stats.needsFollowUp > 0 ? 'text-amber-700' : undefined}
          />
        </div>
      )}

      {/* ---- filters ---- */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, organisation, email or phone"
          className="min-w-60 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        {STAKEHOLDER_TAG_ORDER.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => toggleTag(t)}
            aria-pressed={tagFilter.includes(t)}
            className={[
              'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
              tagFilter.includes(t)
                ? TAG_CHIP[t]
                : 'border-slate-300 text-slate-500 hover:bg-slate-50',
            ].join(' ')}
          >
            {STAKEHOLDER_TAG_LABEL[t]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setStaleOnly((v) => !v)}
          aria-pressed={staleOnly}
          className={[
            'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
            staleOnly
              ? 'border-amber-500 bg-amber-100 text-amber-900'
              : 'border-slate-300 text-slate-500 hover:bg-slate-50',
          ].join(' ')}
        >
          Needs follow-up
        </button>
      </div>

      {/* ---- the list ---- */}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading && rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-400">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-400">
          No contacts match. {total === 0 && 'Add the first one with the button above.'}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {rows.map((c) => {
            const age = contactAge(c.daysSinceContact);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    void stakeholderApi.byId(c.id).then(setSelected).catch(() => undefined);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-slate-800">
                        {c.displayName}
                      </span>
                      {c.tags.map((t) => (
                        <span key={t}
                          className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${TAG_CHIP[t]}`}>
                          {STAKEHOLDER_TAG_LABEL[t]}
                        </span>
                      ))}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {[c.jobTitle, c.organisationName, c.email].filter(Boolean).join(' · ') || '-'}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className={`block text-xs font-medium ${age.tone}`}>{age.label}</span>
                    <span className="block text-[11px] text-slate-400">
                      {c.interactionCount} logged
                    </span>
                  </span>
                  {/* The row opens the panel, where Edit and Remove live.
                      A chevron says so; per-row action buttons would put a
                      delete one mis-tap away in a long list. */}
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-slate-300">
                    <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor"
                      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        noun="contacts"
        pageSizeOptions={PAGE_SIZES}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <SidePanel
        open={selected !== null}
        wide
        title={selected?.displayName ?? ''}
        description={selected?.organisationName ?? undefined}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <StakeholderPanel
            contact={selected}
            onChanged={refreshAfterWrite}
            onDeleted={(id) => {
              // Drop it from the page immediately rather than waiting for
              // the reload - the panel is closing and an empty row left
              // behind it reads as a failed delete.
              setSelected(null);
              setRows((current) => current.filter((r) => r.id !== id));
              setTotal((t) => Math.max(0, t - 1));
              void load();
              stakeholderApi.stats().then(setStats).catch(() => undefined);
            }}
          />
        )}
      </SidePanel>

      <SidePanel
        open={creating}
        title="Add a contact"
        description="Someone outside the organisation"
        onClose={() => setCreating(false)}
      >
        <CreateForm
          onCreated={(contact) => {
            setCreating(false);
            setSelected(contact);
            void load();
            stakeholderApi.stats().then(setStats).catch(() => undefined);
          }}
        />
      </SidePanel>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <span className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
      <span className="text-[11px] text-slate-400">{label} </span>
      <span className={`text-sm font-semibold ${tone ?? 'text-slate-800'}`}>{value}</span>
    </span>
  );
}

/** Name only is enough to save - see the schema's note on why. */
function CreateForm({ onCreated }: { onCreated: (c: StakeholderDetail) => void }) {
  const [displayName, setDisplayName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [tags, setTags] = useState<StakeholderTag[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);

  const canSave = useMemo(() => displayName.trim() !== '', [displayName]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await stakeholderApi.create({
        displayName: displayName.trim(),
        organisationName: organisationName.trim() || null,
        jobTitle: jobTitle.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        location: location.trim() || null,
        tags,
      });
      // A duplicate email is reported, not refused - two people really do
      // share a family address.
      if (result.duplicateOf) setDuplicate(result.duplicateOf.displayName);
      onCreated(result.stakeholder);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4 px-6 py-5">
      <Field label="Name" required value={displayName} onChange={setDisplayName} />
      <Field label="Organisation / party / outlet" value={organisationName} onChange={setOrganisationName} />
      <Field label="Role" value={jobTitle} onChange={setJobTitle} />
      <Field label="Email" value={email} onChange={setEmail} type="email" />
      <Field label="Phone" value={phone} onChange={setPhone} />
      <Field label="Location" value={location} onChange={setLocation} />

      <div>
        <span className="block text-sm font-medium text-slate-700">Tags</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {STAKEHOLDER_TAG_ORDER.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={tags.includes(t)}
              onClick={() => setTags((c) => (c.includes(t) ? c.filter((x) => x !== t) : [...c, t]))}
              className={[
                'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
                tags.includes(t) ? TAG_CHIP[t] : 'border-slate-300 text-slate-500 hover:bg-slate-50',
              ].join(' ')}
            >
              {STAKEHOLDER_TAG_LABEL[t]}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-400">
          A person can be several at once - a donor who is also a party member.
        </p>
      </div>

      {duplicate && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-100">
          {duplicate} already has this email. Saved anyway - check whether
          they are the same person.
        </p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={!canSave || busy}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-60"
      >
        {busy ? 'Saving...' : 'Add contact'}
      </button>
    </form>
  );
}

function Field({
  label, value, onChange, required, type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">
        {label}{required && <span className="ml-1 text-red-500">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
      />
    </label>
  );
}
